import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
// import "../../../css/EnterpriseDetails.css";
import "../../../css/DocumentHistoryResponsive.css";
import filterIcon from "../../../assets/Images/Filter.png";
import changeStatusIcon from "../../../assets/icons/changeStatusIcon.png";
import deleteIcon from "../../../assets/Images/DeleteIcon.png";
import trainDoc from "../../../assets/icons/TrainDoc.png";
import trainText from "../../../assets/icons/TrainText.png";
import trainUrls from "../../../assets/icons/TrainUrl.png";
import trainVideo from "../../../assets/icons/TrainVideo.png";
import trainQA from "../../../assets/icons/TrainQA.png";
import { getFilteredDocs } from "../../../utils/approvedContentTabs";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../../utils/toastMessages";
import { formatFileType } from "../../../utils/FileType";
import viewIcon from "../../../assets/Images/View.png";
import downloadIcon from "../../../assets/Images/Download.png";
import folderIcon from "../../../assets/Images/folder.png";
import pluseIcon from "../../../assets/Images/PlusIcon.png";

const RecentlyDocsFiles = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [allApprovedDocs, setAllApprovedDocs] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname;

  const tabs = [
    { name: "Recently", path: "/user/mydrive-recent" },
    { name: "Uploaded", path: "/user/mydrive-docs" },
    { name: "Saved", path: "/user/mydrive-save" },
    { name: "Deleted", path: "/user/mydrive-delete" },
    // { name: "Q&A", path: "/user/qahistory" },
  ];

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchGlobalUserDocs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/global-user-docs`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAllApprovedDocs(res.data);
      // console.log(res.data);
    } catch (error) {
      console.error("❌ Error fetching approved docs:", error);
      showErrorToast(TOAST_MESSAGES.DOCUMENT_HISTORY.FETCH_FAILED);
    }
  };

  useEffect(() => {
    fetchGlobalUserDocs();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const handleDownload = async (pythonFileUUID) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/download?python_file_uuid=${pythonFileUUID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed: ${errText}`);
      }

      const blob = await response.blob();

      // ✅ Get filename from Content-Disposition header
      let filename = "downloaded_file";
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i
        );
        if (match && match[1]) filename = decodeURIComponent(match[1]);
      }

      // ✅ Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // console.log(`✅ File downloaded: ${filename}`);
    } catch (error) {
      console.error("❌ Download failed:", error);
      showErrorToast("Failed to download file.");
    }
  };

  // Delete function
  const handleDeleteDocument = async (fileId) => {
    // if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await api.delete(
        `${process.env.REACT_APP_API_URL}/uploaded-files/docs/${fileId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showSuccessToast("Document deleted successfully");
      // Refresh data after delete
      fetchGlobalUserDocs();
    } catch (error) {
      console.error("❌ Error deleting document:", error);
      showErrorToast("Failed to delete document");
    }
  };

  const documentFiles = useMemo(
    () => getFilteredDocs(allApprovedDocs, "document"),
    [allApprovedDocs],
  );

  const filteredFiles = useMemo(() => {
    const term = debouncedSearchTerm.toLowerCase();
    return documentFiles
      .filter((file) => file.name?.toLowerCase().includes(term))
      .filter((file) => {
        if (!dayFilter) return true;
        const updatedDate = new Date(file.updatedAt);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dayFilter === "Today") return updatedDate >= today;
        if (dayFilter === "Yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          return updatedDate >= yesterday && updatedDate < today;
        }
        return true;
      })
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt);
        const dateB = new Date(b.updatedAt);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
  }, [documentFiles, debouncedSearchTerm, dayFilter, sortOrder]);

  const totalEntries = filteredFiles.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredFiles.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const statsData = [
    {
      label: "New",
      value: allApprovedDocs.filter((doc) => doc.type === "text").length,
      icon: pluseIcon,
    },
    {
      label: "Uploaded Documents",
      value: getFilteredDocs(allApprovedDocs, "document").length,
      icon: folderIcon,
    },
    {
      label: "Saved Documents",
      value: getFilteredDocs(allApprovedDocs, "url").length,
      icon: folderIcon,
    },
    // {
    //   label: "Uploaded Text",
    //   value: allApprovedDocs.filter((doc) => doc.type === "text").length,
    //   icon: trainText,
    // },
    {
      label: "Deleted Documents",
      value: allApprovedDocs.filter((doc) => doc.type === "youtube").length,
      icon: folderIcon,
    },
    // {
    //   label: "Uploaded Q&A",
    //   value: allApprovedDocs.filter((doc) => doc.type === "qa").length,
    //   icon: trainQA,
    // },
  ];

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 justify-content-evenly align-items-stretch">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div
              style={{ flexDirection: "column", gap: "5px" }}
              className="d-flex   align-items-start"
            >
              {item.icon && (
                <img
                  className="icon-image"
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                />
              )}
              <h6 className="mb-0">{item.label}</h6>
            </div>

            {/* <h2 className="fw-bold">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2> */}

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Uploaded Documents
        </span>
      </div>

      {/* <div className="mb-3 mt-3 tabs-wrapper">
        <div className="d-flex p-2 rounded border tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-button ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div> */}
      <div className="mt-3 tabs-wrapper ">
        <div className="tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-button ${
                currentTab === tab.path ? "active" : " "
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{ borderLeft: "1px solid var(--dark-color) " }}
        className="d-flex px-2 py-1 justify-content-between align-items-center "
      >
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        {/* Search input */}
        <div className="search">
          <div
            className="input-group custom-search-group"
            style={{ width: "320px" }}
          >
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div
          className="not-disable"
          style={{ borderLeft: "1px solid var(--dark-color) " }}
        >
          <div className="table-responsive">
            <table className="table DocHistory-table table-sm w-100">
              <thead className="text-left">
                <tr>
                  <th>Document Name</th>
                  <th>Document Type</th>
                  <th>Document Size</th>
                  <th>Updated At</th>
                  <th>Uploaded Mode</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((file, index) => (
                  <tr key={file.id || index}>
                    <td
                      data-label="Document Name"
                      className="text-truncate text-left"
                      style={{ maxWidth: "150px" }}
                      title={file.name}
                    >
                      {file.name.replace(/\.[^/.]+$/, "")}
                    </td>

                    {/* <td data-label="Document Type" className="text-left">
                      {file.type}
                    </td> */}
                    <td data-label="Document Type" className="text-left">
                      {formatFileType(file.type, file.name)}
                    </td>
                    <td className="text-left" data-label="Document Size">
                      {formatFileSize(file.size)}
                    </td>
                    <td className="text-left" data-label="Updated At">
                      {new Date(file.updatedAt).toLocaleString()}
                    </td>
                    <td className="text-left" data-label="Document Status">
                      {file.mode}
                      {/* <span
                        className={`badge ${
                          file.approval_status === "approved"
                            ? "bg-success"
                            : file.approval_status === "pending"
                            ? "bg-warning"
                            : "bg-danger"
                        }`}
                      >
                        {file.approval_status.charAt(0).toUpperCase() +
                          file.approval_status.slice(1)}
                      </span> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentlyDocsFiles;
